/**
 * Shared materials and constants used across all viewport renderers.
 *
 * Centralizes overlay materials (wireframe, depth prepass), the no-op raycast
 * sentinel, and wireframe opacity constants so that SceneRenderer,
 * PrimitiveRenderer, and MannequinRenderer share identical instances.
 */

import * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Wireframe overlay opacity — shared by all renderers
// ---------------------------------------------------------------------------

/** Opacity for wireframe overlays rendered on top of solid shading. */
export const WIREFRAME_OVERLAY_OPACITY = 0.15;

/** Opacity for solid-surface X-ray shading in object/edit viewport modes. */
export const SURFACE_XRAY_OPACITY = 0.35;

/**
 * Opacity for the gray overlay mesh rendered on top of the object's true
 * material during edit mode. Almost fully opaque — lets a faint tint of the
 * original material bleed through the edit-mode surface tint.
 */
export const EDIT_OVERLAY_OPACITY = 0.92;

// ---------------------------------------------------------------------------
// Shared overlay materials — allocated once, reused everywhere
// ---------------------------------------------------------------------------

/**
 * Depth pre-pass material for non-x-ray wireframe mode.
 * Renders solid geometry into the depth buffer without writing color,
 * so wireframe edges behind surfaces are properly occluded.
 */
export const WIREFRAME_DEPTH_PREPASS_MAT = new THREE.MeshBasicMaterial({
  colorWrite: false,
  depthWrite: true,
  side: THREE.DoubleSide,
});

/** Wireframe overlay rendered on top of solid shading when "Show Wireframe" is on. */
export const WIRE_OVERLAY_MAT = new THREE.MeshBasicMaterial({
  color: "#ffffff",
  wireframe: true,
  transparent: true,
  opacity: WIREFRAME_OVERLAY_OPACITY,
  side: THREE.DoubleSide,
  depthTest: true,
  depthWrite: false,
});

/** X-ray variant — ignores depth so wires show through surfaces. */
export const WIRE_OVERLAY_XRAY_MAT = new THREE.MeshBasicMaterial({
  color: "#ffffff",
  wireframe: true,
  transparent: true,
  opacity: WIREFRAME_OVERLAY_OPACITY,
  side: THREE.DoubleSide,
  depthTest: false,
  depthWrite: false,
});

/**
 * Dark semi-transparent overlay applied to non-sculpt-target objects when in
 * sculpt mode. Renders at renderOrder 3 (above wireframe overlays) to dim
 * the entire object — including wireframe lines — via alpha blending.
 */
export const SCULPT_DIM_MAT = new THREE.MeshBasicMaterial({
  color: 0x000000,
  transparent: true,
  opacity: 0.5,
  depthTest: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});

/**
 * Relax-brush wireframe overlay applied to the mannequin body while the
 * sculpt relax brush is active. Rendered above the base material.
 */
export const RELAX_WIRE_OVERLAY_MAT = new THREE.MeshBasicMaterial({
  color: "#88ccaa",
  wireframe: true,
  transparent: true,
  opacity: 0.35,
  side: THREE.DoubleSide,
});

// ---------------------------------------------------------------------------
// Debug visualization materials — vertex-color shaded, stateless
// ---------------------------------------------------------------------------

/**
 * Topology visualization material for the mannequin. Displays per-vertex
 * colors encoding topology islands, disconnected components, or similar
 * structural data. Paired with a geometry `color` attribute written by the
 * topology-viz effect.
 */
export const TOPOLOGY_VIZ_MAT = new THREE.MeshBasicMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
});

/**
 * Bone-weight visualization material for the mannequin. Displays the active
 * bone's skinning weight as a red→blue vertex color ramp. Paired with a
 * geometry `color` attribute written by the weight-viz effect.
 */
export const BONE_WEIGHT_VIZ_MAT = new THREE.MeshBasicMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
});

/**
 * Diagnostic visualization material for the mannequin (GPU normal buffer
 * viz, morph influence heatmap, UV island viz). Paired with a geometry
 * `color` attribute written by the diag-viz effect.
 */
export const DIAG_VIZ_MAT = new THREE.MeshBasicMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
});

// ---------------------------------------------------------------------------
// No-op raycast sentinel
// ---------------------------------------------------------------------------

/** No-op raycast for overlay meshes that should never be interactive. */
export const noRaycast = () => {};
